import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/lib/useIsAdmin";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/admin/verify-mfa")({
  component: VerifyMfaPage,
});

function VerifyMfaPage() {
  const navigate = useNavigate();
  const { isAdmin, loading } = useIsAdmin();
  const [factorId, setFactorId] = useState<string>("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) { navigate({ to: "/", replace: true }); return; }
    (async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = (data?.totp ?? []).find((f) => f.status === "verified");
      if (!verified) { navigate({ to: "/admin/setup-mfa", replace: true }); return; }
      setFactorId(verified.id);
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === "aal2") navigate({ to: "/admin/puertos", replace: true });
    })();
  }, [loading, isAdmin, navigate]);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !factorId) return;
    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    setVerifying(false);
    if (error) { toast.error(error.message); setCode(""); return; }
    toast.success("Identidad verificada");
    navigate({ to: "/admin/puertos", replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-[#0f0f0f] px-6 py-16 fade-in">
      <WattsLogo size={40} />
      <div className="mt-6 flex items-center gap-2 rounded-full bg-[#F5C518]/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#F5C518]">
        <Shield className="h-3 w-3" /> Admin
      </div>
      <h1 className="mt-6 text-center font-['Barlow_Condensed',sans-serif] text-4xl font-bold uppercase tracking-wide text-white">
        Verifica tu identidad
      </h1>
      <p className="mt-2 max-w-sm text-center text-[13px] text-[#888]">
        Introduce el código de tu app de autenticación
      </p>

      <form onSubmit={verify} className="mt-10 w-full max-w-xs space-y-4">
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
          className="h-16 w-full rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] text-center font-mono text-3xl font-bold tracking-[0.5em] text-[#F5C518] outline-none focus:border-[#F5C518] focus:ring-2 focus:ring-[#F5C518]/30"
        />
        <button
          type="submit"
          disabled={verifying || code.length !== 6}
          className="press-scale h-12 w-full rounded-xl bg-[#F5C518] font-bold text-black disabled:opacity-50"
        >
          {verifying ? "Verificando…" : "Verificar"}
        </button>
      </form>
    </div>
  );
}
